import { marketBetween, regionById } from "../data/worldQueries";
import { worldData } from "../seed";
import { clamp, deterministicHash } from "../utils";
import { eventMatchesAirport } from "./events";
import type {
  Airport,
  AirportConfigSet,
  AirportDepartureOffer,
  AirportFuelConfig,
  AirportFuelQuote,
  AirportOpsConfig,
  AirportPayloadConfig,
  AirportRouteMarketConfig,
  AirportServiceStatus,
  AircraftType,
  GlobalEvent,
  WorldData
} from "../types";

export const DEFAULT_AIRPORT_CONFIG_DATASET_VERSION = "default-airport-config-v2";

type RouteMarketOverride = Pick<AirportRouteMarketConfig, "basePaxPerDay" | "baseCargoKgPerDay" | "yieldUsdPerKm" | "volatility">;

const DEFAULT_ROUTE_MARKET_OVERRIDES: Record<string, RouteMarketOverride> = {
  "PEK-FRA": routeMarketOverride(1_050, 95_000, 0.122, 0.05),
  "FRA-PKX": routeMarketOverride(1_000, 92_000, 0.124, 0.05),
  "PKX-FRA": routeMarketOverride(1_020, 94_000, 0.123, 0.05),
  "FRA-PEK": routeMarketOverride(1_000, 92_000, 0.124, 0.05),
  "PVG-SIN": routeMarketOverride(840, 34_000, 0.158, 0.06),
  "SIN-PVG": routeMarketOverride(820, 34_000, 0.158, 0.06),
  "PVG-PEK": routeMarketOverride(1_520, 24_000, 0.205, 0.04),
  "PEK-PVG": routeMarketOverride(1_520, 24_000, 0.205, 0.04),
  "PVG-CAN": routeMarketOverride(1_360, 22_000, 0.2, 0.04),
  "CAN-PVG": routeMarketOverride(1_360, 22_000, 0.2, 0.04),
  "CAN-BKK": routeMarketOverride(780, 24_000, 0.28, 0.06),
  "BKK-CAN": routeMarketOverride(760, 24_000, 0.28, 0.06),
  "HKG-NRT": routeMarketOverride(760, 28_000, 0.17, 0.06),
  "NRT-HKG": routeMarketOverride(740, 28_000, 0.17, 0.06),
  "PKX-DXB": routeMarketOverride(900, 82_000, 0.136, 0.05),
  "DXB-PKX": routeMarketOverride(880, 82_000, 0.136, 0.05),
  "PVG-SYD": routeMarketOverride(860, 80_000, 0.13, 0.06),
  "SYD-PVG": routeMarketOverride(830, 80_000, 0.13, 0.06),
  "PVG-LAX": routeMarketOverride(1_040, 98_000, 0.121, 0.06),
  "LAX-PVG": routeMarketOverride(1_020, 98_000, 0.121, 0.06)
};

export interface AirportDepartureOfferRequest {
  origin: Airport;
  destination: Airport;
  aircraftType: AircraftType;
  distanceKm: number;
  day: number;
  minute: number;
  events: GlobalEvent[];
  originOps: AirportOpsConfig;
  destinationOps: AirportOpsConfig;
  originPayload: AirportPayloadConfig;
  originFuel: AirportFuelConfig;
  destinationFuel: AirportFuelConfig;
  routeMarket: AirportRouteMarketConfig;
}

// D1 bootstrap and tests only. Runtime rules receive scoped, resolved config.
export function buildDefaultAirportConfigSet(world: WorldData = worldData): AirportConfigSet {
  return {
    ops: world.airports.map((airport) => ({
      airportCode: airport.code,
      slotCapacity: airport.slotCapacity,
      departureCapacity: defaultDepartureCapacity(airport.slotCapacity),
      arrivalCapacity: defaultArrivalCapacity(airport.slotCapacity),
      minimumTurnMinutes: defaultMinimumTurnMinutes(airport.slotCapacity),
      groundHandlingLevel: clamp(Number((airport.slotCapacity / 78).toFixed(2)), 0.75, 1.35),
      closed: false
    })),
    fuel: world.airports.map((airport) => {
      const zone = world.fuelZones.find((item) => item.id === airport.fuelZoneId);
      return {
        airportCode: airport.code,
        basePriceUsdPerTon: zone?.baseUsdPerTon ?? 900,
        taxIndex: zone?.taxIndex ?? 1,
        supplyLimitKgPerDay: defaultFuelSupplyLimitKgPerDay(airport.slotCapacity),
        refuelRateKgPerMin: defaultRefuelRateKgPerMin(airport.slotCapacity),
        minUpliftKg: 0,
        maxUpliftKg: defaultMaxUpliftKg(airport.slotCapacity),
        fuelAvailable: true
      };
    }),
    payload: world.airports.map((airport) => {
      const region = world.regions.find((item) => item.id === airport.regionId);
      const demandIndex = region?.demandIndex ?? 1;
      return {
        airportCode: airport.code,
        baseDepartureDemand: defaultBaseDepartureDemand(airport.slotCapacity, demandIndex),
        cargoDemandIndex: Number((0.85 + airport.slotCapacity / 120).toFixed(2)),
        baggageKgPerPassenger: 18,
        paxProcessingCapacity: defaultPaxProcessingCapacity(airport.slotCapacity),
        cargoProcessingCapacityKg: defaultCargoProcessingCapacityKg(airport.slotCapacity)
      };
    }),
    routeMarkets: buildDefaultAirportRouteMarkets(world)
  };
}

export function buildAirportDepartureOffer(request: AirportDepartureOfferRequest): AirportDepartureOffer {
  const originOps = request.originOps;
  const destinationOps = request.destinationOps;
  const payload = request.originPayload;
  const routeMarket = request.routeMarket;
  const originEvents = request.events.filter((event) => eventMatchesAirport(event, request.origin));
  const destinationEvents = request.events.filter((event) => eventMatchesAirport(event, request.destination));
  const appliedEvents = [...originEvents, ...destinationEvents.filter((event) => !originEvents.some((originEvent) => originEvent.id === event.id))];
  const originCapacityMultiplier = eventMultiplier(originEvents, "airport_capacity_multiplier");
  const destinationCapacityMultiplier = eventMultiplier(destinationEvents, "airport_capacity_multiplier");
  const demandMultiplier = eventMultiplier(appliedEvents, "demand_multiplier");
  const originCapacityScore = airportCapacityScore(originOps, originCapacityMultiplier, "departure");
  const destinationCapacityScore = airportCapacityScore(destinationOps, destinationCapacityMultiplier, "arrival");
  const capacityScore = Math.min(originCapacityScore, destinationCapacityScore);
  const status = airportServiceStatus(originOps.closed || destinationOps.closed, capacityScore);
  const deterministicVolatility = routeMarketDeterministicVolatility(routeMarket, request);
  const rawDemand = routePassengerDemand(routeMarket, payload, deterministicVolatility, demandMultiplier);
  const offeredPassengers = airportOfferPassengers(request.aircraftType, payload, rawDemand, capacityScore);

  return {
    airportCode: request.origin.code,
    destinationCode: request.destination.code,
    generatedDay: request.day,
    generatedMinute: request.minute,
    payloadOffer: {
      passengers: offeredPassengers,
      baggageKg: airportOfferBaggageKg(offeredPassengers, payload),
      cargoKg: airportOfferCargoKg(routeMarket, payload, demandMultiplier, capacityScore),
      yieldUsdPerKm: routeMarket.yieldUsdPerKm,
      demandMultiplier: Number(demandMultiplier.toFixed(2)),
      reasons: [
        `航线客流 ${Math.round(routeMarket.basePaxPerDay)} pax/day`,
        `机场客流 ${Math.round(payload.baseDepartureDemand)} pax/day`,
        `机场处理 ${Math.round(payload.paxProcessingCapacity)} pax`,
        ...eventReasons(appliedEvents, "demand_multiplier")
      ]
    },
    fuelQuote: buildAirportFuelQuote(request.origin, request.events, request.originFuel),
    destinationFuelQuote: buildAirportFuelQuote(request.destination, request.events, request.destinationFuel),
    handling: {
      status,
      capacityMultiplier: Number(capacityScore.toFixed(2)),
      expectedGroundDelayMinutes: airportGroundDelayMinutes(status, capacityScore),
      blockers: airportHandlingBlockers(originOps, destinationOps, status),
      reasons: [
        `Slot 容量 ${originOps.slotCapacity}/h`,
        `出港容量 ${originOps.departureCapacity}/h`,
        `到达容量 ${destinationOps.arrivalCapacity}/h`,
        `地服等级 ${originOps.groundHandlingLevel}`,
        ...eventReasons(originEvents, "airport_capacity_multiplier"),
        ...eventReasons(destinationEvents, "airport_capacity_multiplier")
      ]
    },
    appliedEventIds: appliedEvents.map((event) => event.id)
  };
}

export function buildAirportFuelQuote(airport: Airport, events: GlobalEvent[], config: AirportFuelConfig): AirportFuelQuote {
  const appliedEvents = events.filter((event) => eventMatchesAirport(event, airport));
  const priceDelta = appliedEvents.reduce((sum, event) => {
    return (
      sum +
      event.effects.reduce((inner, effect) => {
        if (effect.type === "fuel_price_delta") {
          return inner + effect.usdPerTonDelta;
        }
        return inner;
      }, 0)
    );
  }, 0);
  const capacityMultiplier = eventMultiplier(appliedEvents, "airport_capacity_multiplier");
  const available = config.fuelAvailable && capacityMultiplier >= 0.2;
  const fuelCapacity = airportFuelCapacity(config, available, capacityMultiplier);

  return {
    airportCode: airport.code,
    available,
    priceUsdPerTon: airportFuelPriceUsdPerTon(config, priceDelta),
    maxUpliftKg: fuelCapacity.maxUpliftKg,
    refuelRateKgPerMin: fuelCapacity.refuelRateKgPerMin,
    expectedFuelingDelayMinutes: fuelCapacity.expectedFuelingDelayMinutes,
    reasons: [
      airportFuelBaselineReason(config),
      ...eventReasons(appliedEvents, "fuel_price_delta"),
      ...eventReasons(appliedEvents, "airport_capacity_multiplier")
    ]
  };
}

function routeMarketDeterministicVolatility(routeMarket: AirportRouteMarketConfig, request: AirportDepartureOfferRequest): number {
  const dayComponent = request.day * 1.9;
  const originCodeComponent = request.origin.code.charCodeAt(0);
  const destinationCodeComponent = request.destination.code.charCodeAt(1);
  const marketHashComponent = deterministicHash(routeMarket.id) % 17;
  const volatilitySeed = dayComponent + originCodeComponent + destinationCodeComponent + marketHashComponent;
  const volatilityOffset = Math.sin(volatilitySeed) * routeMarket.volatility;
  return 1 + volatilityOffset;
}

function routePassengerDemand(routeMarket: AirportRouteMarketConfig, payload: AirportPayloadConfig, deterministicVolatility: number, demandMultiplier: number): number {
  const routeDemandLimit = routeMarket.basePaxPerDay;
  const airportDemandLimit = payload.baseDepartureDemand;
  const baseDemand = Math.min(routeDemandLimit, airportDemandLimit);
  return baseDemand * deterministicVolatility * demandMultiplier;
}

function airportOfferPassengers(aircraftType: AircraftType, payload: AirportPayloadConfig, rawDemand: number, capacityScore: number): number {
  const seatCapacityLimit = aircraftType.seatCapacity * 0.94;
  const demandLimit = rawDemand * 0.28;
  const protectedCapacityScore = protectedOfferCapacityScore(capacityScore);
  const processingLimit = payload.paxProcessingCapacity * protectedCapacityScore;
  const offeredPassengerLimit = Math.min(seatCapacityLimit, demandLimit, processingLimit);
  const roundedPassengers = Math.round(offeredPassengerLimit);
  return Math.max(0, roundedPassengers);
}

function airportOfferBaggageKg(offeredPassengers: number, payload: AirportPayloadConfig): number {
  const baggageDemandKg = offeredPassengers * payload.baggageKgPerPassenger;
  return Math.round(baggageDemandKg);
}

function airportOfferCargoKg(routeMarket: AirportRouteMarketConfig, payload: AirportPayloadConfig, demandMultiplier: number, capacityScore: number): number {
  const routeCargoDemandKg = routeMarket.baseCargoKgPerDay * payload.cargoDemandIndex * demandMultiplier;
  const protectedCapacityScore = protectedOfferCapacityScore(capacityScore);
  const processingCapacityKg = payload.cargoProcessingCapacityKg * protectedCapacityScore;
  const offeredCargoLimitKg = Math.min(routeCargoDemandKg, processingCapacityKg);
  const roundedCargoKg = Math.round(offeredCargoLimitKg);
  return Math.max(0, roundedCargoKg);
}

function protectedOfferCapacityScore(capacityScore: number): number {
  return Math.max(0.25, capacityScore);
}

function airportFuelPriceUsdPerTon(config: AirportFuelConfig, priceDelta: number): number {
  const taxedBasePriceUsdPerTon = config.basePriceUsdPerTon * config.taxIndex;
  const rawPriceUsdPerTon = taxedBasePriceUsdPerTon + priceDelta;
  const roundedPriceUsdPerTon = Math.round(rawPriceUsdPerTon);
  return Math.max(450, roundedPriceUsdPerTon);
}

function airportServiceStatus(closed: boolean, capacityScore: number): AirportServiceStatus {
  if (closed || capacityScore < 0.2) {
    return "closed";
  }
  if (capacityScore < 0.65) {
    return "restricted";
  }
  return "open";
}

function airportCapacityScore(airportOps: AirportOpsConfig, eventCapacityMultiplier: number, movementKind: "departure" | "arrival"): number {
  if (airportOps.closed) {
    return 0;
  }
  const constrainedCapacity = Math.min(airportOps.slotCapacity, airportMovementCapacity(airportOps, movementKind));
  const normalizedCapacity = constrainedCapacity / 45;
  const boundedCapacity = clamp(normalizedCapacity, 0.25, 1.2);
  return airportOps.groundHandlingLevel * eventCapacityMultiplier * boundedCapacity;
}

function airportMovementCapacity(airportOps: AirportOpsConfig, movementKind: "departure" | "arrival"): number {
  if (movementKind === "departure") {
    return airportOps.departureCapacity;
  }
  return airportOps.arrivalCapacity;
}

function airportHandlingBlockers(originOps: AirportOpsConfig, destinationOps: AirportOpsConfig, status: AirportServiceStatus): string[] {
  const blockers: string[] = [];
  if (originOps.closed) {
    blockers.push(`${originOps.airportCode} 机场关闭，不能放行。`);
  }
  if (destinationOps.closed) {
    blockers.push(`${destinationOps.airportCode} 机场关闭，不能放行。`);
  }
  if (!originOps.closed && !destinationOps.closed && status === "closed") {
    blockers.push(`${originOps.airportCode}-${destinationOps.airportCode} 机场容量不足，不能放行。`);
  }
  return blockers;
}

function airportFuelCapacity(config: AirportFuelConfig, available: boolean, capacityMultiplier: number): Pick<AirportFuelQuote, "maxUpliftKg" | "refuelRateKgPerMin" | "expectedFuelingDelayMinutes"> {
  if (!available) {
    return {
      maxUpliftKg: 0,
      refuelRateKgPerMin: 0,
      expectedFuelingDelayMinutes: 0
    };
  }

  const dailySupplyWindowKg = config.supplyLimitKgPerDay * 0.22;
  const cappedByAirportLimitKg = Math.min(config.maxUpliftKg, dailySupplyWindowKg);
  const roundedCappedUpliftKg = Math.round(cappedByAirportLimitKg);
  const maxUpliftKg = Math.max(config.minUpliftKg, roundedCappedUpliftKg);

  const protectedCapacityMultiplier = Math.max(0.25, capacityMultiplier);
  const rawRefuelRateKgPerMin = config.refuelRateKgPerMin * protectedCapacityMultiplier;
  const roundedRefuelRateKgPerMin = Math.round(rawRefuelRateKgPerMin);
  const refuelRateKgPerMin = Math.max(0, roundedRefuelRateKgPerMin);

  const capacityShortfall = Math.max(0, 0.75 - capacityMultiplier);
  const expectedFuelingDelayMinutes = Math.round(capacityShortfall * 60);

  return {
    maxUpliftKg,
    refuelRateKgPerMin,
    expectedFuelingDelayMinutes
  };
}

function airportFuelBaselineReason(config: AirportFuelConfig): string {
  if (config.fuelAvailable) {
    const supplyTonsPerDay = Math.round(config.supplyLimitKgPerDay / 1000);
    return `基础供油 ${supplyTonsPerDay}t/day`;
  }
  return "基础配置暂停供油";
}

function defaultDepartureCapacity(slotCapacity: number): number {
  const rawCapacity = slotCapacity * 0.7;
  const roundedCapacity = Math.round(rawCapacity);
  return Math.max(18, roundedCapacity);
}

function defaultArrivalCapacity(slotCapacity: number): number {
  const rawCapacity = slotCapacity * 0.65;
  const roundedCapacity = Math.round(rawCapacity);
  return Math.max(18, roundedCapacity);
}

function defaultMinimumTurnMinutes(slotCapacity: number): number {
  if (slotCapacity >= 75) {
    return 58;
  }
  if (slotCapacity >= 60) {
    return 68;
  }
  return 82;
}

function defaultRefuelRateKgPerMin(slotCapacity: number): number {
  if (slotCapacity >= 75) {
    return 1250;
  }
  if (slotCapacity >= 60) {
    return 950;
  }
  return 650;
}

function defaultFuelSupplyLimitKgPerDay(slotCapacity: number): number {
  const rawSupplyLimitKgPerDay = slotCapacity * 7_500;
  const protectedSupplyLimitKgPerDay = Math.max(90_000, rawSupplyLimitKgPerDay);
  return Math.round(protectedSupplyLimitKgPerDay);
}

function defaultMaxUpliftKg(slotCapacity: number): number {
  const rawMaxUpliftKg = slotCapacity * 950;
  const protectedMaxUpliftKg = Math.max(18_000, rawMaxUpliftKg);
  return Math.round(protectedMaxUpliftKg);
}

function defaultBaseDepartureDemand(slotCapacity: number, demandIndex: number): number {
  const rawBaseDepartureDemand = slotCapacity * 18 * demandIndex;
  const protectedBaseDepartureDemand = Math.max(700, rawBaseDepartureDemand);
  return Math.round(protectedBaseDepartureDemand);
}

function defaultPaxProcessingCapacity(slotCapacity: number): number {
  const rawPaxProcessingCapacity = slotCapacity * 25;
  const protectedPaxProcessingCapacity = Math.max(900, rawPaxProcessingCapacity);
  return Math.round(protectedPaxProcessingCapacity);
}

function defaultCargoProcessingCapacityKg(slotCapacity: number): number {
  const rawCargoProcessingCapacityKg = slotCapacity * 1_500;
  const protectedCargoProcessingCapacityKg = Math.max(35_000, rawCargoProcessingCapacityKg);
  return Math.round(protectedCargoProcessingCapacityKg);
}

function airportGroundDelayMinutes(status: AirportServiceStatus, capacityScore: number): number {
  if (status === "closed") {
    return 0;
  }
  if (capacityScore < 0.75) {
    return Math.round((0.75 - capacityScore) * 90);
  }
  return 0;
}

function buildDefaultAirportRouteMarkets(world: WorldData): AirportRouteMarketConfig[] {
  const markets: AirportRouteMarketConfig[] = [];
  for (const origin of world.airports) {
    for (const destination of world.airports) {
      if (origin.code === destination.code) {
        continue;
      }
      const regionalMarket = marketBetween(origin.regionId, destination.regionId, world);
      const originRegion = regionById(origin.regionId, world);
      const destinationRegion = regionById(destination.regionId, world);
      const sameRegion = origin.regionId === destination.regionId;
      const basePaxPerDay = regionalMarket?.basePaxPerDay ?? Math.round(360 * (originRegion?.demandIndex ?? 1) * (destinationRegion?.demandIndex ?? 1));
      const routeScale = defaultRouteScale(sameRegion);
      const cargoKgPerPassenger = defaultCargoKgPerPassenger(sameRegion);
      const yieldUsdPerKm = regionalMarket?.yieldUsdPerKm ?? defaultYieldUsdPerKm(sameRegion);
      const override = defaultRouteMarketOverride(origin.code, destination.code);
      markets.push({
        id: `${origin.code}-${destination.code}`,
        originAirportCode: origin.code,
        destinationAirportCode: destination.code,
        basePaxPerDay: override?.basePaxPerDay ?? defaultRoutePaxPerDay(basePaxPerDay, routeScale),
        baseCargoKgPerDay: override?.baseCargoKgPerDay ?? defaultRouteCargoKgPerDay(basePaxPerDay, cargoKgPerPassenger),
        yieldUsdPerKm: override?.yieldUsdPerKm ?? yieldUsdPerKm,
        volatility: override?.volatility ?? regionalMarket?.volatility ?? 0.08
      });
    }
  }
  return markets;
}

function routeMarketOverride(basePaxPerDay: number, baseCargoKgPerDay: number, yieldUsdPerKm: number, volatility: number): RouteMarketOverride {
  return { basePaxPerDay, baseCargoKgPerDay, yieldUsdPerKm, volatility };
}

function defaultRouteMarketOverride(originCode: string, destinationCode: string): RouteMarketOverride | undefined {
  return DEFAULT_ROUTE_MARKET_OVERRIDES[`${originCode}-${destinationCode}`];
}

function defaultRoutePaxPerDay(basePaxPerDay: number, routeScale: number): number {
  const scaledPaxPerDay = basePaxPerDay * routeScale;
  const roundedPaxPerDay = Math.round(scaledPaxPerDay);
  return Math.max(45, roundedPaxPerDay);
}

function defaultRouteCargoKgPerDay(basePaxPerDay: number, cargoKgPerPassenger: number): number {
  const rawCargoKgPerDay = basePaxPerDay * cargoKgPerPassenger;
  const roundedCargoKgPerDay = Math.round(rawCargoKgPerDay);
  return Math.max(1_200, roundedCargoKgPerDay);
}

function defaultRouteScale(sameRegion: boolean): number {
  if (sameRegion) {
    return 0.42;
  }
  return 0.22;
}

function defaultCargoKgPerPassenger(sameRegion: boolean): number {
  if (sameRegion) {
    return 12;
  }
  return 18;
}

function defaultYieldUsdPerKm(sameRegion: boolean): number {
  if (sameRegion) {
    return 0.105;
  }
  return 0.128;
}


function eventMultiplier(events: GlobalEvent[], effectType: "airport_capacity_multiplier" | "demand_multiplier"): number {
  return events.reduce((multiplier, event) => {
    return (
      multiplier *
      event.effects.reduce((inner, effect) => {
        if (effectType === "airport_capacity_multiplier" && effect.type === "airport_capacity_multiplier") return inner * effect.multiplier;
        if (effectType === "demand_multiplier" && effect.type === "demand_multiplier") return inner * effect.multiplier;
        return inner;
      }, 1)
    );
  }, 1);
}

function eventReasons(events: GlobalEvent[], effectType: "airport_capacity_multiplier" | "demand_multiplier" | "fuel_price_delta"): string[] {
  return events.flatMap((event) =>
    event.effects.flatMap((effect) => {
      if (effectType === "fuel_price_delta" && effect.type === "fuel_price_delta") {
        return `${event.title}: 油价 ${fuelPriceDeltaSign(effect.usdPerTonDelta)}${effect.usdPerTonDelta}/t`;
      }
      if (effectType === "airport_capacity_multiplier" && effect.type === "airport_capacity_multiplier") {
        return `${event.title}: 容量 x${effect.multiplier}`;
      }
      if (effectType === "demand_multiplier" && effect.type === "demand_multiplier") {
        return `${event.title}: 客流 x${effect.multiplier}`;
      }
      return [];
    })
  );
}

function fuelPriceDeltaSign(value: number): string {
  if (value >= 0) {
    return "+";
  }
  return "";
}
